import React, { useEffect, useRef } from 'react';
import { ArrowUpIcon, CameraIcon, MapPinIcon, MicIcon, SparklesIcon } from 'lucide-react';
interface PromptBoxProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  /** Bouton photo affiche uniquement si un gestionnaire est fourni. */
  onAddPhoto?: () => void;
  /** `hero` is the single-line pill shown before the conversation starts. */
  variant: 'hero' | 'chat';
  placeholder: string;
  busy?: boolean;
  photoAttached?: boolean;
  autoFocus?: boolean;
}
const toolButton = 'grid h-10 w-10 shrink-0 place-items-center rounded-full text-slate-500 transition-colors duration-150 ease-ville hover:bg-slate-100 hover:text-ville-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo';
export function PromptBox({
  value,
  onChange,
  onSubmit,
  onAddPhoto,
  variant,
  placeholder,
  busy = false,
  photoAttached = false,
  autoFocus = false
}: PromptBoxProps) {
  const areaRef = useRef<HTMLTextAreaElement | null>(null);
  const inputId = variant === 'hero' ? 'request-input' : 'request-reply';
  useEffect(() => {
    const el = areaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 168)}px`;
  }, [value, variant]);
  useEffect(() => {
    if (!autoFocus) return;
    const timer = window.setTimeout(() => areaRef.current?.focus(), 320);
    return () => window.clearTimeout(timer);
  }, [autoFocus]);
  const send = () => {
    if (busy) return;
    onSubmit();
  };
  if (variant === 'hero') {
    return <form onSubmit={event => {
      event.preventDefault();
      send();
    }} className="flex items-center gap-2 rounded-full border border-slate-200 bg-white py-2.5 pl-5 pr-2.5 shadow-lg shadow-ville-navy/5 transition-colors duration-150 ease-ville focus-within:border-ville-indigo" data-id="element-783" data-mp="1vstnnhjndbqc">
        <SparklesIcon className="h-5 w-5 shrink-0 text-ville-indigo" aria-hidden="true" data-id="element-784" data-mp="2elfc3gs8600w" />
        <label htmlFor={inputId} className="sr-only" data-id="element-785" data-mp="2ejkvgenzku2m">
          What would you like to report?
        </label>
        <input id={inputId} type="text" value={value} onChange={event => onChange(event.target.value)} placeholder={placeholder} className="min-w-0 flex-1 border-0 bg-transparent py-2.5 text-base text-ville-navy placeholder:text-slate-400 focus:outline-none sm:text-lg" data-id="element-786" data-mp="1h6ui10ef4avh" />
        {onAddPhoto && <button type="button" onClick={onAddPhoto} aria-label="Add a photo" className={toolButton} data-id="element-787" data-mp="1v0lgndqm2acf">
            <CameraIcon className="h-5 w-5" aria-hidden="true" data-id="element-788" data-mp="1zzokwvyrocjc" />
          </button>}
        <button type="button" aria-label="Use my location" className={`hidden sm:grid ${toolButton}`} data-id="element-789" data-mp="24xggq12j8ist">
          <MapPinIcon className="h-5 w-5" aria-hidden="true" data-id="element-790" data-mp="28w5ys6yimois" />
        </button>
        <button type="button" aria-label="Dictate the description" className={toolButton} data-id="element-791" data-mp="ak8dpm4el0w3">
          <MicIcon className="h-5 w-5" aria-hidden="true" data-id="element-792" data-mp="el0owkwu5fv0" />
        </button>
        <button type="submit" aria-label="Send the description to ATNA" className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-ville-lime text-ville-navy transition-transform duration-150 ease-ville hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo focus-visible:ring-offset-2" data-id="element-793" data-mp="70zto8kjaxsv">
          <ArrowUpIcon className="h-5 w-5" aria-hidden="true" data-id="element-794" data-mp="pzc5k22tyls5" />
        </button>
      </form>;
  }
  return <form onSubmit={event => {
    event.preventDefault();
    send();
  }} className="rounded-[24px] border border-slate-200 bg-white px-4 pb-3 pt-3.5 transition-colors duration-150 ease-ville focus-within:border-ville-indigo" data-id="element-795" data-mp="kzzl1nzflqsq">
      <label htmlFor={inputId} className="sr-only" data-id="element-796" data-mp="c1x9wflzhhrq">
        Reply to ATNA
      </label>
      <textarea id={inputId} ref={areaRef} rows={1} value={value} onChange={event => onChange(event.target.value)} onKeyDown={event => {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        send();
      }
    }} placeholder={placeholder} className="block max-h-[168px] w-full resize-none border-0 bg-transparent px-1 text-base leading-relaxed text-ville-navy placeholder:text-slate-400 focus:outline-none sm:text-[17px]" data-id="element-797" data-mp="31qg5ctx9taot" />

      <div className="mt-1.5 flex items-center justify-between gap-3" data-id="element-798" data-mp="177lj2l7zepvx">
        <div className="flex items-center gap-0.5" data-id="element-799" data-mp="1c6y0gqg334r4">
          {onAddPhoto && <button type="button" onClick={onAddPhoto} aria-label="Attach a photo" aria-pressed={photoAttached} className={photoAttached ? 'grid h-10 w-10 shrink-0 place-items-center rounded-full bg-ville-indigo-soft text-ville-indigo focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo' : toolButton} data-id="element-800" data-mp="1c4ka2uwcmgpw">
              <CameraIcon className="h-5 w-5" aria-hidden="true" data-id="element-801" data-mp="3fdvpv4vhrq7z" />
            </button>}
          <button type="button" aria-label="Use my location" className={toolButton} data-id="element-802" data-mp="3bdbrjxz6mgk9">
            <MapPinIcon className="h-5 w-5" aria-hidden="true" data-id="element-803" data-mp="3pcbi5b6995j2" />
          </button>
          <button type="button" aria-label="Dictate your reply" className={toolButton} data-id="element-804" data-mp="3ke9zusgnla8n">
            <MicIcon className="h-5 w-5" aria-hidden="true" data-id="element-805" data-mp="2wofo6e82lrpz" />
          </button>
          {photoAttached && <span className="ml-1 text-xs font-semibold text-ville-indigo" data-id="element-806" data-mp="2rqe3xpywrmcx">1 photo attached</span>}
        </div>

        <button type="submit" disabled={busy || value.trim().length === 0} aria-label="Send to ATNA" className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-ville-lime text-ville-navy transition-[transform,opacity] duration-150 ease-ville hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400 disabled:hover:translate-y-0" data-id="element-807" data-mp="36f1utixt77wj">
          <ArrowUpIcon className="h-5 w-5" aria-hidden="true" data-id="element-808" data-mp="htrrsm7dlnba" />
        </button>
      </div>
    </form>;
}